import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { COMPANY_COLORS } from '@/constants/colors.constant'
import { useRiskOneYearHistory } from '@/contexts/RADashboardDataContext/hooks'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import {
  cardPrefsContextKey,
  CardProps,
} from '@/interfaces/raDashboard.interface'
import { RiskHistoryDateStats } from '@/services/report-dashboard/raDashboard.data.service'
import { putUserPreference } from '@/services/userPreference.service'
import GridOnIcon from '@mui/icons-material/GridOn'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import TimelineIcon from '@mui/icons-material/Timeline'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import GridActionsMenu, { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { getGridThemeClass } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  ColDef,
  GridReadyEvent,
  ModuleRegistry,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import dayjs from 'dayjs'
import customParseFormats from 'dayjs/plugin/customParseFormat'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

dayjs.extend(customParseFormats)

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule,
  ExcelExportModule,
  MenuModule,
  ClipboardModule,
  RangeSelectionModule,
])

enum TrailingRiskDayOptions {
  FiveDays = '5 Days',
  FifteenDays = '15 Days',
  ThirtyDays = '30 Days',
  NinetyDays = '90 Days',
  OneEightyDays = '180 Days',
  OneYear = '1 Year',
}
enum DataPlottingOptions {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}
const CHART_OPTIONS = ['Line Chart', 'Area Chart', 'Combo Chart']

// MUI FilterAltIcon SVG, replaces ag-grid's default filter glyph (matches Holdings)
const FILTER_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 6h10l-5.01 6.3L7 6zm-2.75-.39C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39c.51-.66.04-1.61-.79-1.61H5.04c-.83 0-1.3.95-.79 1.61z"/></svg>'

interface TrailingRiskRow {
  analysisDate: string
  portfolioTotalRisk: number
  benchmarkTotalRisk: number
  activeTotalRisk: number
  period_var: number
}

const dayCountMap: Record<TrailingRiskDayOptions, number> = {
  [TrailingRiskDayOptions.FiveDays]: 5,
  [TrailingRiskDayOptions.FifteenDays]: 15,
  [TrailingRiskDayOptions.ThirtyDays]: 30,
  [TrailingRiskDayOptions.NinetyDays]: 90,
  [TrailingRiskDayOptions.OneEightyDays]: 180,
  [TrailingRiskDayOptions.OneYear]: 365,
}
const dayOptionEnumerable = Object.values(TrailingRiskDayOptions)

const TrailingRiskCard: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const oneYearHistory = useRiskOneYearHistory()
  const earliestDayAvailable = oneYearHistory?.earliestDateAvailable
    ? dayjs(oneYearHistory?.earliestDateAvailable)
    : null
  const latestDayAvailable = oneYearHistory?.latestDateAvailable
    ? dayjs(oneYearHistory?.latestDateAvailable)
    : null

  const [selectedDays, setSelectedDays] = useState<TrailingRiskDayOptions>(
    () => {
      const saved = props.cardPrefs?.trailingRisk?.selectedDays
      return saved &&
        dayOptionEnumerable.includes(saved as TrailingRiskDayOptions)
        ? (saved as TrailingRiskDayOptions)
        : TrailingRiskDayOptions.FiveDays
    },
  )
  const [drillMonth, setDrillMonth] = useState<string | null>(null)
  const [selectedChart, setSelectedChart] = useState(() => {
    const saved = props.cardPrefs?.trailingRisk?.selectedChart
    return saved && CHART_OPTIONS.includes(saved) ? saved : 'Line Chart'
  })
  const [showTable, setShowTable] = useState(
    () => props.cardPrefs?.trailingRisk?.showTable ?? false,
  )

  useEffect(() => {
    const tr = props.cardPrefs?.trailingRisk
    if (!tr) return
    if (
      dayOptionEnumerable.includes(tr.selectedDays as TrailingRiskDayOptions)
    ) {
      setSelectedDays(tr.selectedDays as TrailingRiskDayOptions)
    }
    if (CHART_OPTIONS.includes(tr.selectedChart)) {
      setSelectedChart(tr.selectedChart)
    }
    setShowTable(tr.showTable)
  }, [props.cardPrefs])

  const savePrefs = (
    days: TrailingRiskDayOptions,
    chart: string,
    table: boolean,
  ) => {
    if (props.cardPrefs?.dashboardName) {
      const newPrefs = {
        ...props.cardPrefs,
        trailingRisk: {
          selectedDays: days,
          selectedChart: chart,
          showTable: table,
        },
      }
      putUserPreference(
        cardPrefsContextKey(props.cardPrefs.dashboardName),
        newPrefs,
      )
      props.onCardPrefsChange?.(newPrefs)
    }
  }

  const [daysMenuAnchor, setDaysMenuAnchor] = useState<null | HTMLElement>(null)
  const [chartMenuAnchor, setChartMenuAnchor] = useState<null | HTMLElement>(
    null,
  )

  const drillMonthFormat = 'YYYY-MM-DD'

  const dayDifference = dayCountMap[selectedDays]
  let plotOption = DataPlottingOptions.Daily
  if (drillMonth != null) {
    plotOption = DataPlottingOptions.Daily
  } else if (dayDifference >= 180) {
    // 6 Months+ of data
    // Only add 1 point per month
    plotOption = DataPlottingOptions.Monthly
  } else if (dayDifference > 30) {
    // Over a month
    // Only add 1 point per week
    plotOption = DataPlottingOptions.Weekly
  }

  const disabledDayOptions: Record<string, boolean> = useMemo(() => {
    return dayOptionEnumerable.reduce(
      (obj, opt, i, arr): Record<string, boolean> => {
        if (
          i == 0 ||
          earliestDayAvailable == null ||
          latestDayAvailable == null
        ) {
          obj[opt] = false // If we dont have a date for comparison or it is the first option, always enable the option.
        } else {
          const prevOpt = arr[i - 1]
          if (obj[prevOpt]) {
            // If the previous option was disabled, all following that should be as well.
            obj[opt] = true
          } else {
            // Otherwise see if the previous option was within the earliest day available
            const endDay = latestDayAvailable
            const dayCount = dayCountMap[prevOpt]
            const startDay =
              dayCount === 365
                ? endDay.add(-1, 'year')
                : endDay.add(-dayCount, 'day')
            obj[opt] = startDay.isBefore(earliestDayAvailable) // Disable if the earliest date we want is before the earliest date available.
          }
        }
        return obj
      },
      {},
    )
  }, [])

  const cardTitle = CardTitleLookup[props.cardId]
  // Table mode always shows the full 1-year Daily history; chart respects selectedDays + drill.
  let tableTitle: string
  if (showTable) {
    tableTitle = `1 Year Daily ${cardTitle}`
  } else if (drillMonth != null) {
    tableTitle = `${drillMonth} Daily ${cardTitle}`
  } else {
    tableTitle = `${selectedDays} ${cardTitle}`
    if (plotOption != DataPlottingOptions.Daily) {
      tableTitle += ` (${plotOption})`
    }
  }

  // Builds the row set for either the chart (sampled per plotOption + optional month drill,
  // limited to selectedDays) or the table (full 1-year Daily data, no drill, ignores selectedDays).
  const buildPlotData = (
    plot: DataPlottingOptions,
    drill: string | null,
    useFullRange: boolean = false,
  ): TrailingRiskRow[] => {
    if (oneYearHistory == null) return []
    const { historicalData } = oneYearHistory
    const effectiveDayDifference = useFullRange
      ? historicalData.length
      : dayDifference
    // "N Days" = the N most recent daily observations (trading days), not a
    // calendar window — so 5 Days shows 5 data points even across weekends.
    // (historicalData is sorted ascending and already capped at ~1 year.)
    const windowed = historicalData.slice(-effectiveDayDifference)
    const drillMonthDay = drill === null ? null : dayjs(drill, drillMonthFormat)
    let data: TrailingRiskRow[] = []
    if (plot === DataPlottingOptions.Monthly) {
      let monthlyData: Record<string, RiskHistoryDateStats> = {}
      windowed.forEach((dayStats) => {
        const { analysisDate } = dayStats
        const day = dayjs(analysisDate)
        const monthStr = day.format('YYYY-MM')
        const currentLatest = monthlyData[monthStr]
        if (!currentLatest || day.isAfter(dayjs(currentLatest.analysisDate))) {
          monthlyData[monthStr] = dayStats
        }
      })
      Object.entries(monthlyData)
        // Sort since we are not guaranteed to still be in date order
        .sort(
          ([key1, a], [key2, b]) =>
            a.analysisDate.getTime() - b.analysisDate.getTime(),
        )
        .forEach(([analysisDate, dayStats]) => {
          data.push({
            ...dayStats,
            analysisDate: dayjs(dayStats.analysisDate).format('YYYY-MM-DD'),
          })
        })
    } else if (plot === DataPlottingOptions.Weekly) {
      let weeklyData: Record<string, RiskHistoryDateStats> = {}
      windowed.forEach((dayStats) => {
        const { analysisDate } = dayStats
        const day = dayjs(analysisDate)
        const weekStr = `${day.day(1).format('MMM DD')} — ${day.day(5).format('MMM DD YYYY')}`
        const currentLatest = weeklyData[weekStr]
        if (!currentLatest || day.isAfter(dayjs(currentLatest.analysisDate))) {
          weeklyData[weekStr] = dayStats
        }
      })
      Object.entries(weeklyData)
        // Sort since we are not guaranteed to still be in date order
        .sort(
          ([key1, a], [key2, b]) =>
            a.analysisDate.getTime() - b.analysisDate.getTime(),
        )
        .forEach(([analysisDate, dayStats]) => {
          data.push({
            ...dayStats,
            analysisDate: dayjs(dayStats.analysisDate).format('YYYY-MM-DD'),
          })
        })
    } else {
      windowed.forEach((dayStats) => {
        const { analysisDate } = dayStats
        const day = dayjs(analysisDate)
        if (drill != null) {
          // If we are drilling into a month, only plot data from that month
          if (!day.isSame(drillMonthDay, 'month')) return
        }
        data.push({
          ...dayStats,
          analysisDate: day.format('YYYY-MM-DD'),
        })
      })
    }
    return data
  }

  // Chart respects the dynamic plotOption + drillMonth (Daily/Weekly/Monthly sampling).
  const chartData = useMemo(
    () => buildPlotData(plotOption, drillMonth),
    [oneYearHistory, selectedDays, plotOption, drillMonth],
  )
  // Table always shows the full 1-year Daily history, regardless of the chart's selectedDays / drill.
  const tableData = useMemo(
    () => buildPlotData(DataPlottingOptions.Daily, null, true),
    [oneYearHistory],
  )

  // Table view is an ag-grid so it can export the full data set to CSV / Excel.
  const gridRef = useRef<AgGridReact>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => gridRef.current?.api)

  const riskValueFormatter = (p: ValueFormatterParams) =>
    p.value == null ? '' : Number(p.value).toFixed(2)

  // Headers stay left-aligned (default); only the numeric cells are right-aligned
  // via cellClass — matching the Holdings grid.
  const tableColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Date',
        field: 'analysisDate',
        flex: 1,
        // analysisDate is a display string ('YYYY-MM-DD'); sort and filter it as a real date.
        comparator: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterDate: Date, cellValue: string) => {
            if (!cellValue) return 0
            const cell = new Date(cellValue)
            cell.setHours(0, 0, 0, 0)
            const target = new Date(filterDate)
            target.setHours(0, 0, 0, 0)
            if (cell.getTime() < target.getTime()) return -1
            if (cell.getTime() > target.getTime()) return 1
            return 0
          },
        },
      },
      { headerName: 'Portfolio Risk (%)', field: 'portfolioTotalRisk', flex: 1, cellClass: 'ag-right-aligned-cell', valueFormatter: riskValueFormatter, filter: 'agNumberColumnFilter' },
      { headerName: 'Benchmark Risk (%)', field: 'benchmarkTotalRisk', flex: 1, cellClass: 'ag-right-aligned-cell', valueFormatter: riskValueFormatter, filter: 'agNumberColumnFilter' },
      { headerName: 'Tracking Error (%)', field: 'activeTotalRisk', flex: 1, cellClass: 'ag-right-aligned-cell', valueFormatter: riskValueFormatter, filter: 'agNumberColumnFilter' },
    ],
    [],
  )

  const defaultTableColDef = useMemo<ColDef>(
    () => ({ sortable: true, filter: true, resizable: true }),
    [],
  )

  // Daily data for the currently-selected range — drives chart-view export so it
  // matches the active day filter (15d -> 15 days, 180d -> 180 days of daily data),
  // rather than the chart's downsampled (weekly/monthly) points.
  const exportRangeData = useMemo(
    () => buildPlotData(DataPlottingOptions.Daily, drillMonth, false),
    [oneYearHistory, selectedDays, drillMonth],
  )

  const onTableGridReady = useCallback((e: GridReadyEvent) => {
    e.api.sizeColumnsToFit()
  }, [])

  // Raw, full-precision values for copy/export (ignore the 2-decimal display formatter).
  const rawValueCallback = useCallback((params: any) => {
    const field = params?.column?.getColDef?.()?.field
    const data = params?.node?.data
    return field && data && data[field] !== undefined ? data[field] : params.value
  }, [])

  // Run a copy/export against the current view; in chart view, isolate from any
  // leftover table sort/filter and restore afterward.
  const runIsolated = useCallback(
    (exec: () => void) => {
      const api = gridRef.current?.api
      if (!api) return
      if (showTable) {
        exec()
        return
      }
      const filterModel = api.getFilterModel()
      const columnState = api.getColumnState()
      api.setFilterModel(null)
      api.applyColumnState({ defaultState: { sort: null } })
      exec()
      api.setFilterModel(filterModel)
      api.applyColumnState({ state: columnState, applyOrder: true })
    },
    [showTable],
  )

  // Right-click menu: copy items emit raw values (match export), not the 2-decimal display format.
  const gridContextMenuItems = useMemo(
    () => buildCopyContextMenuItems({ processCell: rawValueCallback }),
    [rawValueCallback],
  )

  // The grid stays mounted in chart view (hidden), so re-fit columns when the
  // table becomes visible — otherwise it was sized against a 0-width container.
  useEffect(() => {
    if (showTable) gridRef.current?.api?.sizeColumnsToFit()
  }, [showTable])

  const chartOptions = useMemo((): Highcharts.Options => {
    const categories = chartData.map((r) => r.analysisDate)
    const portfolioData = chartData.map((r) => r.portfolioTotalRisk)
    const benchmarkData = chartData.map((r) => r.benchmarkTotalRisk)
    const trackingData = chartData.map((r) => r.activeTotalRisk)

    const isArea = selectedChart === 'Area Chart'
    const isCombo = selectedChart === 'Combo Chart'

    // Combo: columns for risk series, line for tracking error
    // Area: area for risk series, line for tracking error
    // Line: line for all
    const riskSeriesType = isCombo ? 'column' : isArea ? 'area' : 'line'
    const trackingSeriesType = 'line'

    return {
      chart: {
        backgroundColor: theme.cardBackground,
        style: { fontFamily: "'Noto Sans', sans-serif" },
        animation: false,
        spacing: [8, 8, 8, 8],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: theme.textPrimary,
          fontSize: '14px',
          fontWeight: '400',
        },
        itemHoverStyle: { color: theme.textPrimary },
      },
      xAxis: {
        categories,
        labels: {
          style: { color: theme.textPrimary, fontSize: '14px' },
          // Daily views (5/15/30 days and drilled month): label every business
          // day, rotated when dense so they fit. Weekly (90d) / Monthly (180d,
          // 1yr) keep thinned, horizontal labels so they stay uncluttered.
          ...(plotOption === DataPlottingOptions.Daily
            ? { step: 1, rotation: chartData.length > 10 ? -45 : 0 }
            : { step: Math.ceil(chartData.length / 10) }),
        },
        lineColor: theme.border,
        tickColor: theme.border,
        gridLineColor: theme.border,
      },
      yAxis: [
        {
          title: {
            text: 'Risk (%)',
            style: { color: theme.textPrimary, fontSize: '14px' },
          },
          labels: {
            format: '{value}%',
            style: { color: theme.textPrimary, fontSize: '14px' },
          },
          min: 0,
          gridLineColor: theme.border,
          lineColor: theme.border,
        },
      ],
      tooltip: {
        shared: true,
        backgroundColor: theme.cardBackground,
        borderColor: theme.border,
        style: { color: theme.textPrimary, fontSize: '14px' },
        headerFormat:
          '<span style="font-size:12px;font-weight:600">{point.key}</span><br/>',
        pointFormatter: function (this: Highcharts.Point) {
          const unit = '%';
          const val = `${(this.y as number).toFixed(2)}${unit}`;
          return `<span style="color:${this.series.color}">\u25CF</span> ${this.series.name}: <b>${val}</b><br/>`
        },
      },
      plotOptions: {
        line: {
          lineWidth: 1.5,
          marker: { enabled: true, radius: 4 },
        },
        area: {
          lineWidth: 2,
          marker: { enabled: true, radius: 4 },
        },
        column: {
          grouping: true,
          pointPadding: 0.1,
          groupPadding: 0.25,
          borderWidth: 0,
        },
        series: {
          // Tracking error line stays above both areas at all times (zIndex 3).
          // Hovering an area fills it solid and raises it only above its
          // sibling area (zIndex 2 vs 1) so it reads clearly on top of the
          // other translucent area; mouseOut restores it, but the line is
          // never covered, hovered or not.
          events: {
            mouseOver: function (this: Highcharts.Series) {
              if (this.type !== 'area') return
              this.update({ fillOpacity: 1, zIndex: 2 } as Highcharts.SeriesAreaOptions, true)
            },
            mouseOut: function (this: Highcharts.Series) {
              if (this.type !== 'area') return
              this.update({ fillOpacity: 0.5, zIndex: 1 } as Highcharts.SeriesAreaOptions, true)
            },
          },
          point: {
            events: {
              click: function (e) {
                if (plotOption === DataPlottingOptions.Monthly) {
                  if (typeof this.category === 'string')
                    setDrillMonth(this.category)
                }
              },
            },
          },
        },
      },
      series: [
        {
          type: riskSeriesType,
          name: 'Portfolio Risk',
          data: portfolioData,
          color: COMPANY_COLORS.DARK_BLUE,
          fillOpacity: 0.5,
          yAxis: 0,
          threshold: 0,
          zIndex: 1,
        },
        {
          type: riskSeriesType,
          name: 'Benchmark Risk',
          data: benchmarkData,
          color: COMPANY_COLORS.NEUTRAL_GREY,
          fillOpacity: 0.5,
          yAxis: 0,
          threshold: 0,
          zIndex: 1,
        },
        {
          type: trackingSeriesType,
          name: 'Tracking Error',
          data: trackingData,
          color: COMPANY_COLORS.LIGHT_BLUE,
          yAxis: 0,
          lineWidth: isCombo ? 2.5 : 1.5,
          marker: { radius: isCombo ? 5 : 4 },
          zIndex: 3,
        },
      ],
    }
  }, [chartData, selectedChart, theme, plotOption])

  const cardLayout = {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    padding: '12px',
    boxSizing: 'border-box',
  }

  const dropdownSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textPrimary,
    backgroundColor: theme.cardBackground,
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
  }

  const menuPaperSx = {
    backgroundColor: theme.cardBackground,
    border: `1px solid ${theme.border}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    minWidth: 140,
  }

  return (
    <Stack
      sx={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      {/* Header */}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: '12px' }}
      >
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, color: theme.textPrimary }}
        >
          {tableTitle}
        </Typography>

        {drillMonth && (
          <Box
            onClick={() => setDrillMonth(null)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              fontSize: '14px',
              color: theme.textPrimary,
              cursor: 'pointer',
              backgroundColor: theme.cardBackground,
              whiteSpace: 'nowrap' as const,
              '&:hover': { backgroundColor: theme.border },
            }}
          >
            <KeyboardArrowLeftIcon sx={{ fontSize: 14 }} />
            Back To Monthly
          </Box>
        )}

        <Stack direction='row' alignItems='center' spacing={1}>
          {/* Days dropdown — chart only; table always shows 1 year */}
          {!showTable && (
            <>
              <Box
                sx={dropdownSx}
                onClick={(e) => setDaysMenuAnchor(e.currentTarget)}
              >
                {selectedDays}
                <KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />
              </Box>
              <Menu
                anchorEl={daysMenuAnchor}
                open={Boolean(daysMenuAnchor)}
                onClose={() => setDaysMenuAnchor(null)}
                PaperProps={{ sx: menuPaperSx }}
              >
                {dayOptionEnumerable.map((opt, i, arr) => (
                  <MenuItem
                    key={opt}
                    onClick={() => {
                      setSelectedDays(opt)
                      setDaysMenuAnchor(null)
                      savePrefs(opt, selectedChart, showTable)
                    }}
                    disabled={!!disabledDayOptions[opt]}
                    sx={{
                      fontSize: '14px',
                      color: theme.textPrimary,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      '&:hover': { backgroundColor: theme.border },
                    }}
                  >
                    {opt}
                    {opt === selectedDays && (
                      <Typography component='span' sx={{ fontSize: '14px' }}>
                        ✓
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Chart type dropdown — hidden in table view */}
          {!showTable && (
            <>
              <Box
                sx={dropdownSx}
                onClick={(e) => setChartMenuAnchor(e.currentTarget)}
              >
                {selectedChart}
                <KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />
              </Box>
              <Menu
                anchorEl={chartMenuAnchor}
                open={Boolean(chartMenuAnchor)}
                onClose={() => setChartMenuAnchor(null)}
                PaperProps={{ sx: menuPaperSx }}
              >
                {CHART_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt}
                    onClick={() => {
                      setSelectedChart(opt)
                      setChartMenuAnchor(null)
                      savePrefs(selectedDays, opt, showTable)
                    }}
                    sx={{
                      fontSize: '14px',
                      color: theme.textPrimary,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      '&:hover': { backgroundColor: theme.border },
                    }}
                  >
                    {opt}
                    {opt === selectedChart && (
                      <Typography component='span' sx={{ fontSize: '14px' }}>
                        ✓
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Export/copy menu (3-dot) — shared across grids */}
          <GridActionsMenu
            getApi={() => gridRef.current?.api}
            fileName='risk_data'
            processCellCallback={rawValueCallback}
            wrapRun={runIsolated}
          />

          {/* Chart / Table toggle */}
          <IconButton
            size='small'
            onClick={() => {
              const next = !showTable
              setShowTable(next)
              savePrefs(selectedDays, selectedChart, next)
            }}
            sx={{
              borderRadius: '6px',
              padding: '5px',
              backgroundColor: theme.cardBackground,
              border: `1px solid ${theme.border}`,
              color: theme.textPrimary,
              '&:hover': {
                backgroundColor: theme.border,
              },
            }}
          >
            {showTable ? (
              <TimelineIcon sx={{ fontSize: 16 }} />
            ) : (
              <GridOnIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Stack>
      </Stack>

      {/* Content */}
      {/* Chart — shown when not in table view */}
      {!showTable && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            updateArgs={[true, true, false]}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
        </Box>
      )}

      {/* Table (ag-grid) — kept mounted even in chart view so the export menu
          can download the full data set from either view. */}
      <Box
        ref={gridBoxRef}
        className={getGridThemeClass(isDarkMode)}
        sx={[
          {
            flex: showTable ? 1 : 'unset',
            display: showTable ? 'block' : 'none',
            minHeight: 0,
            overflow: 'hidden',
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            // cwan theme drives colors; keep only the vars it doesn't define.
            '--ag-secondary-foreground-color': theme.textSecondary,
            '--ag-control-panel-background-color': theme.cardBackground,
            '--ag-input-border-color': theme.border,
            '--ag-grid-size': '4px',
          } as React.CSSProperties,
          gridHeaderIconsSx,
        ]}
      >
        <AgGridReact
          ref={gridRef}
          containerStyle={{ height: '100%', width: '100%' }}
          rowData={showTable ? tableData : exportRangeData}
          columnDefs={tableColumnDefs}
          defaultColDef={defaultTableColDef}
          onGridReady={onTableGridReady}
          columnHoverHighlight
          icons={{ filter: () => FILTER_ICON_SVG }}
          enableRangeSelection
          popupParent={document.body}
          getContextMenuItems={gridContextMenuItems}
          processCellForClipboard={rawValueCallback}
          data-cy='trailing-risk-grid'
        />
      </Box>
    </Stack>
  )
}

export default TrailingRiskCard
